/**
 * Client-side API Client for Enterprise UI
 *
 * This file contains client-only code for browser API communication.
 * For server-side requests in loaders/actions, use client.server.ts
 */

import { authClient } from '../firebase';
import { getEnterpriseApiBaseUrl } from '../env';

export async function getAuthToken(): Promise<string | null> {
  try {
    const auth = authClient();
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    const idTokenResult = await user.getIdTokenResult(false);
    const expiresIn = new Date(idTokenResult.expirationTime).getTime() - Date.now();
    const refreshThreshold = 5 * 60 * 1000;
    if (expiresIn < refreshThreshold) {
      return await user.getIdToken(true);
    }
    return idTokenResult.token;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * 認証ヘッダ（Authorization Bearer or x-dev-email fallback）を構築する。
 * fetchWithAuth・fetchResumePdfBlob 共通で使う唯一の認証ロジック。
 */
async function buildAuthHeaders(extra?: HeadersInit): Promise<Headers> {
  const headers = new Headers(extra);
  if (headers.has('Authorization')) {
    return headers;
  }

  const token = await getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  } else if (import.meta.env.DEV) {
    // Development mode fallback
    headers.set('x-dev-email', 'enterprise@example.com');
  }
  return headers;
}

/**
 * Client-side fetch with authentication (JSON 用)
 */
export async function fetchWithAuth(path: string, options: RequestInit = {}) {
  const headers = await buildAuthHeaders(options.headers);
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const url = `${getEnterpriseApiBaseUrl()}${path}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} - ${errorText}`);
  }

  if (response.status === 204) {
    return null;
  }

  const responseText = await response.text();
  if (!responseText) {
    return null;
  }

  return JSON.parse(responseText);
}

export function parseApiErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) return fallback;
  const match = error.message.match(/API Error: \d+ - ([\s\S]+)/);
  if (!match) return error.message;

  try {
    const parsed = JSON.parse(match[1]);
    return typeof parsed?.message === 'string' && parsed.message.length > 0 ? parsed.message : fallback;
  } catch {
    return fallback;
  }
}

export async function fetchRawWithAuth(path: string, options: RequestInit = {}) {
  const headers = await buildAuthHeaders(options.headers);
  if (!(options.body instanceof FormData) && options.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const url = `${getEnterpriseApiBaseUrl()}${path}`;

  return fetch(url, {
    ...options,
    headers,
  });
}

export const api = {
  get: (path: string) => fetchWithAuth(path, { method: 'GET' }),
  post: (path: string, body: unknown) =>
    fetchWithAuth(path, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  put: (path: string, body: unknown) =>
    fetchWithAuth(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  patch: (path: string, body: unknown) =>
    fetchWithAuth(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: (path: string) => fetchWithAuth(path, { method: 'DELETE' }),
};

/**
 * Content-Disposition ヘッダから filename を抽出する。
 * RFC 5987 の filename*=UTF-8''... と従来の filename="..." の両方に対応。
 */
function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;\s]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through
    }
  }
  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ?? null;
}

export interface ResumePdfResponse {
  blob: Blob;
  filename: string;
}

export interface FetchResumePdfOptions {
  /**
   * ダイアログを閉じたタイミングなどで取得を中断するための AbortSignal。
   */
  signal?: AbortSignal;
}

/**
 * 候補者の最新マスキング合格済み職務経歴書を Blob と匿名化済みファイル名で取得する。
 * サーバが Content-Disposition で filename を返すので、クライアントは内部IDを
 * 触らずに済む。
 *
 * signal を渡すと fetch を中断できる。
 */
export async function fetchResumePdfBlob(talentId: string, options: FetchResumePdfOptions = {}): Promise<ResumePdfResponse> {
  const headers = await buildAuthHeaders();
  const url = `${getEnterpriseApiBaseUrl()}/api/v1/enterprise/talents/${talentId}/resume.pdf`;
  const response = await fetch(url, { method: 'GET', headers, signal: options.signal });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error('Failed to fetch resume PDF', { status: response.status, body: errorText });
    throw new Error('職務経歴書の取得に失敗しました。時間をおいて再度お試しください。');
  }

  const blob = await response.blob();
  const filename = parseFilenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? 'resume.pdf';

  return { blob, filename };
}
